import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { subscribeApiLoading } from "../api";

const LoaderContext = createContext({
  isLoading: false,
  activeCount: 0,
  startLoading: () => {},
  stopLoading: () => {},
  withLoader: async (fn) => fn(),
});

export function LoaderProvider({ children }) {
  const [activeRequests, setActiveRequests] = useState(0);
  const [manualCount, setManualCount] = useState(0);

  const startLoading = useCallback(() => setManualCount((c) => c + 1), []);
  const stopLoading = useCallback(() => setManualCount((c) => Math.max(0, c - 1)), []);

  useEffect(() => {
    const unsubscribe = subscribeApiLoading((delta) => {
      setActiveRequests((count) => Math.max(0, count + delta));
    });
    return unsubscribe;
  }, []);

  const withLoader = useCallback(
    async (fn) => {
      startLoading();
      try {
        return await fn();
      } finally {
        stopLoading();
      }
    },
    [startLoading, stopLoading]
  );

  const total = activeRequests + manualCount;

  const value = useMemo(
    () => ({
      isLoading: total > 0,
      activeCount: total,
      startLoading,
      stopLoading,
      withLoader,
    }),
    [total, startLoading, stopLoading, withLoader]
  );

  return <LoaderContext.Provider value={value}>{children}</LoaderContext.Provider>;
}

export function useLoader() {
  return useContext(LoaderContext);
}
